import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  Clock,
  Gauge,
  Layers,
  MessageSquare,
  Server,
  ShoppingCart,
  Timer,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, ApiError } from '../api/client';
import type {
  FailureRow,
  LimiterBucket,
  Overview,
  TimeseriesPoint,
  TopClient,
  TopProject,
} from '../api/types';
import { Badge, TypeBadge } from '../components/Badge';
import { Card, PageHeader } from '../components/Card';
import { ErrorBanner, LoadingBlock } from '../components/Feedback';
import { SegmentedControl } from '../components/Form';
import { StatCard, StatGrid } from '../components/StatCard';
import { DataTable, Mono } from '../components/Table';
import { ago, dateTime, money, num, percent, RANGES, seconds, time } from '../lib/format';

const tooltipStyle = {
  backgroundColor: '#18181b',
  border: '1px solid #3f3f46',
  borderRadius: 8,
  fontSize: 12,
  color: '#f4f4f5',
} as const;

function ChartCard({
  title,
  data,
  loading,
  error,
  onRetry,
}: {
  title: string;
  data: TimeseriesPoint[] | undefined;
  loading: boolean;
  error: ApiError | null;
  onRetry: () => void;
}) {
  return (
    <Card title={title} noPadding bodyClassName="p-2 sm:p-3">
      {error ? (
        <ErrorBanner message={error.message} onRetry={onRetry} />
      ) : loading && !data ? (
        <LoadingBlock />
      ) : !data || data.length === 0 ? (
        <div className="py-10 text-center text-xs text-zinc-500">Нет данных за период</div>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="g-created" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g-delivered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g-failed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f87171" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickFormatter={(value: string) => time(value)}
                tick={{ fontSize: 11 }}
                minTickGap={32}
              />
              <YAxis tick={{ fontSize: 11 }} width={36} allowDecimals={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(value: string) => dateTime(value)}
                formatter={(value: number, name: string) => [num(value), name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="created" name="создано" stroke="#38bdf8" fill="url(#g-created)" strokeWidth={1.5} />
              <Area type="monotone" dataKey="delivered" name="выдано" stroke="#34d399" fill="url(#g-delivered)" strokeWidth={1.5} />
              <Area type="monotone" dataKey="failed" name="ошибки" stroke="#f87171" fill="url(#g-failed)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function MoneyChartCard({
  data,
  loading,
  error,
  onRetry,
}: {
  data: TimeseriesPoint[] | undefined;
  loading: boolean;
  error: ApiError | null;
  onRetry: () => void;
}) {
  return (
    <Card title="Выручка и маржа ($)" noPadding bodyClassName="p-2 sm:p-3">
      {error ? (
        <ErrorBanner message={error.message} onRetry={onRetry} />
      ) : loading && !data ? (
        <LoadingBlock />
      ) : !data || data.length === 0 ? (
        <div className="py-10 text-center text-xs text-zinc-500">Нет данных за период</div>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickFormatter={(value: string) => time(value)}
                tick={{ fontSize: 11 }}
                minTickGap={32}
              />
              <YAxis tick={{ fontSize: 11 }} width={44} />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(value: string) => dateTime(value)}
                formatter={(value: number, name: string) => [money(value), name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="выручка" fill="#38bdf8" radius={[2, 2, 0, 0]} />
              <Bar dataKey="margin" name="маржа" fill="#34d399" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

const rangeLabel = (hours: number) => RANGES.find((r) => r.hours === hours)?.label ?? `${hours} ч`;

export function DashboardPage() {
  const [hours, setHours] = useState(24);

  const overviewPath = `/admin-api/stats/overview?hours=${hours}`;
  const timeseriesPath = `/admin-api/stats/timeseries?hours=${hours}`;
  const topProjectsPath = `/admin-api/stats/top-projects?hours=${hours}`;
  const topClientsPath = `/admin-api/stats/top-clients?hours=${hours}`;
  const failuresPath = `/admin-api/stats/failures?hours=${hours}`;

  const overviewQuery = useQuery<Overview, ApiError>({
    queryKey: ['api', overviewPath],
    queryFn: ({ signal }) => api.get<Overview>(overviewPath, signal),
    refetchInterval: 10_000,
    retry: 1,
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
  });

  const tsQuery = useQuery<TimeseriesPoint[], ApiError>({
    queryKey: ['api', timeseriesPath],
    queryFn: ({ signal }) =>
      api.get<{ list: TimeseriesPoint[] }>(timeseriesPath, signal).then((r) => r.list),
    refetchInterval: 10_000,
    retry: 1,
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
  });

  const topProjectsQuery = useQuery<TopProject[], ApiError>({
    queryKey: ['api', topProjectsPath],
    queryFn: ({ signal }) =>
      api.get<{ list: TopProject[] }>(topProjectsPath, signal).then((r) => r.list),
    refetchInterval: 30_000,
    retry: 1,
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
  });

  const topClientsQuery = useQuery<TopClient[], ApiError>({
    queryKey: ['api', topClientsPath],
    queryFn: ({ signal }) =>
      api.get<{ list: TopClient[] }>(topClientsPath, signal).then((r) => r.list),
    refetchInterval: 30_000,
    retry: 1,
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
  });

  const failuresQuery = useQuery<FailureRow[], ApiError>({
    queryKey: ['api', failuresPath],
    queryFn: ({ signal }) =>
      api.get<{ list: FailureRow[] }>(failuresPath, signal).then((r) => r.list),
    refetchInterval: 30_000,
    retry: 1,
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
  });

  const o = overviewQuery.data;
  const limiter: LimiterBucket[] = o?.limiter ?? [];
  const banned = limiter.filter((b) => b.banned_until);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Обзор"
        description={
          o
            ? `Диапазон: ${rangeLabel(hours)} · обновлено ${ago(new Date().toISOString())}`
            : 'Загрузка…'
        }
        actions={
          <SegmentedControl
            value={hours}
            options={RANGES.map((r) => ({ label: r.label, value: r.hours }))}
            onChange={(value) => setHours(value)}
          />
        }
      />

      {overviewQuery.error ? (
        <ErrorBanner message={overviewQuery.error.message} onRetry={() => overviewQuery.refetch()} />
      ) : null}

      {o ? (
        <>
          <StatGrid>
            <StatCard label="Покупки" value={num(o.purchases.total)} hint={`выдано ${num(o.purchases.delivered)} · ошибок ${num(o.purchases.failed)}`} icon={<ShoppingCart className="size-4" />} />
            <StatCard label="Успешность" value={percent(o.purchases.success_rate)} tone={o.purchases.success_rate >= 90 ? 'good' : o.purchases.success_rate >= 70 ? 'warn' : 'bad'} icon={<Gauge className="size-4" />} />
            <StatCard label="Ср. выдача" value={seconds(o.purchases.avg_delivery_seconds)} hint={`p95 ${seconds(o.purchases.p95_delivery_seconds)}`} icon={<Clock className="size-4" />} />
            <StatCard label="Выручка" value={money(o.money.revenue)} hint={`за ${rangeLabel(hours)}`} tone="good" icon={<Banknote className="size-4" />} />
            <StatCard label="Маржа" value={money(o.money.margin)} hint={o.money.margin_percent !== null ? `${percent(o.money.margin_percent)} от себестоимости` : undefined} tone="good" icon={<ArrowUpRight className="size-4" />} />
            <StatCard label="Себестоимость" value={money(o.money.cost)} icon={<ArrowDownRight className="size-4" />} />
          </StatGrid>

          <StatGrid>
            <StatCard label="SMS-коды" value={percent(o.sms.code_rate)} hint={`с кодом ${num(o.sms.numbers_with_code)} из ${num(o.sms.numbers_assigned)}`} icon={<MessageSquare className="size-4" />} />
            <StatCard label="Ср. время до кода" value={seconds(o.sms.avg_time_to_code_seconds)} icon={<Timer className="size-4" />} />
            <StatCard label="Очередь" value={num(o.queue.queued + o.queue.processing)} hint={`в очереди ${num(o.queue.queued)} · в работе ${num(o.queue.processing)}`} tone={o.queue.queued > 0 ? 'warn' : 'default'} icon={<Activity className="size-4" />} />
            <StatCard label="Пул доступен" value={num(o.pool.available)} hint={`всего в пуле ${num(o.pool.total)}`} icon={<Layers className="size-4" />} />
            <StatCard label="Расход за час" value={money(o.upstream.spend_hour)} hint={`за сутки ${money(o.upstream.spend_day)}`} icon={<Wallet className="size-4" />} />
            <StatCard label="Вызовы upstream" value={num(o.upstream.calls)} hint={`ошибок ${num(o.upstream.calls_failed)} · ср. ${o.upstream.avg_call_ms ?? '—'} мс`} icon={<Server className="size-4" />} />
          </StatGrid>

          {banned.length > 0 ? (
            <div className="flex flex-col gap-2 rounded-lg border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-200 sm:flex-row sm:items-center">
              <div className="flex min-w-0 items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-400" />
                <span className="font-medium">Лимитер забанен:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {banned.map((b) => (
                  <Badge key={b.key} tone="danger">
                    {b.key} до {dateTime(b.banned_until)}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Покупки во времени" data={tsQuery.data} loading={tsQuery.isLoading} error={tsQuery.error} onRetry={() => tsQuery.refetch()} />
            <MoneyChartCard data={tsQuery.data} loading={tsQuery.isLoading} error={tsQuery.error} onRetry={() => tsQuery.refetch()} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Топ проекты" noPadding>
              <DataTable<TopProject>
                columns={[
                  { key: 'name', header: 'Проект', render: (r) => (
                    <div className="flex items-center gap-2">
                      <TypeBadge type={r.type} />
                      <span className="truncate">{r.name}</span>
                    </div>
                  ) },
                  { key: 'purchases', header: 'Пок.', align: 'right', render: (r) => num(r.purchases) },
                  { key: 'delivered', header: 'Выдано', align: 'right', hide: 'sm', render: (r) => num(r.delivered) },
                  { key: 'revenue', header: 'Выручка', align: 'right', render: (r) => money(r.revenue) },
                  { key: 'margin', header: 'Маржа', align: 'right', hide: 'md', render: (r) => money(r.margin) },
                ]}
                rows={topProjectsQuery.data}
                rowKey={(r) => r.project_id}
                loading={topProjectsQuery.isLoading}
                error={topProjectsQuery.error}
                onRetry={() => topProjectsQuery.refetch()}
                emptyTitle="Нет покупок за период"
              />
            </Card>

            <Card title="Топ клиенты" noPadding>
              <DataTable<TopClient>
                columns={[
                  { key: 'name', header: 'Клиент', render: (r) => <span className="truncate">{r.name}</span> },
                  { key: 'purchases', header: 'Пок.', align: 'right', render: (r) => num(r.purchases) },
                  { key: 'delivered', header: 'Выдано', align: 'right', hide: 'sm', render: (r) => num(r.delivered) },
                  { key: 'revenue', header: 'Выручка', align: 'right', render: (r) => money(r.revenue) },
                  { key: 'balance', header: 'Баланс', align: 'right', hide: 'md', render: (r) => money(r.balance) },
                ]}
                rows={topClientsQuery.data}
                rowKey={(r) => r.client_id}
                loading={topClientsQuery.isLoading}
                error={topClientsQuery.error}
                onRetry={() => topClientsQuery.refetch()}
                emptyTitle="Нет клиентов за период"
              />
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Аккаунты upstream" noPadding>
              <div className="divide-y divide-zinc-800">
                {o.upstream.accounts.map((acc) => (
                  <div key={acc.id} className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-zinc-100">{acc.name}</span>
                        <Badge tone={acc.enabled ? 'success' : 'muted'}>{acc.enabled ? 'вкл' : 'выкл'}</Badge>
                      </div>
                      {acc.last_error ? (
                        <div className="mt-0.5 truncate text-xs text-red-400">{acc.last_error}</div>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <div className="tabular text-sm text-zinc-100">{money(acc.balance)}</div>
                      <div className="text-[11px] text-zinc-500">{acc.balance_checked_at ? ago(acc.balance_checked_at) : '—'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Лимитер" noPadding>
              {limiter.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-500">Нет данных</div>
              ) : (
                <DataTable<LimiterBucket>
                  columns={[
                    { key: 'key', header: 'Ключ', render: (r) => <Mono>{r.key}</Mono> },
                    { key: 'tokens', header: 'Токены', align: 'right', render: (r) => `${num(r.tokens)}/${num(r.capacity)}` },
                    { key: 'per_min', header: '/мин', align: 'right', hide: 'sm', render: (r) => num(r.per_min) },
                    { key: 'banned', header: 'Бан', render: (r) =>
                      r.banned_until ? <Badge tone="danger">до {dateTime(r.banned_until)}</Badge> : <Badge tone="muted">нет</Badge>,
                    },
                  ]}
                  rows={limiter}
                  rowKey={(r) => r.key}
                  minWidth="32rem"
                />
              )}
            </Card>
          </div>

          <Card title="Причины ошибок" noPadding>
            <DataTable<FailureRow>
              columns={[
                { key: 'reason', header: 'Причина', render: (r) => <span className="break-all">{r.reason}</span> },
                { key: 'count', header: 'Кол-во', align: 'right', width: '6rem', render: (r) => num(r.count) },
              ]}
              rows={failuresQuery.data}
              rowKey={(r, i) => `${i}-${r.reason}`}
              loading={failuresQuery.isLoading}
              error={failuresQuery.error}
              onRetry={() => failuresQuery.refetch()}
              emptyTitle="Ошибок за период нет"
              minWidth="24rem"
            />
          </Card>
        </>
      ) : overviewQuery.isLoading ? (
        <LoadingBlock label="Загрузка статистики…" />
      ) : null}
    </div>
  );
}
